import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import "./Dittes.css";

const configuredApiBase = process.env.REACT_APP_API_BASE;
const API_BASE =
  configuredApiBase && !configuredApiBase.includes("localhost:3002")
    ? configuredApiBase
    : `${window.location.origin}/api`;

function formatDateTime(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("de-AT", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatEventMeta(startValue, endValue) {
  const start = new Date(startValue);
  const end = new Date(endValue);
  const sameDate = start.toDateString() === end.toDateString();

  if (sameDate) {
    const datePart = new Intl.DateTimeFormat("de-AT", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(start);
    const timePart = `${new Intl.DateTimeFormat("de-AT", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(start)}-${new Intl.DateTimeFormat("de-AT", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(end)} Uhr`;
    return `${datePart}, ${timePart}`;
  }

  return `${formatDateTime(startValue)} bis ${formatDateTime(endValue)}`;
}

function sameDay(date, isoValue) {
  const d = new Date(isoValue);
  return d.getFullYear() === date.getFullYear() && d.getMonth() === date.getMonth() && d.getDate() === date.getDate();
}

function normalizeDay(value) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
}

function isDateWithinEvent(date, startIso, endIso) {
  const current = normalizeDay(date);
  const start = normalizeDay(startIso);
  const end = normalizeDay(endIso);
  return current >= start && current <= end;
}

function getEventSpanClass(date, startIso, endIso) {
  const current = normalizeDay(date);
  const start = normalizeDay(startIso);
  const end = normalizeDay(endIso);
  const isStart = current.getTime() === start.getTime();
  const isEnd = current.getTime() === end.getTime();

  if (isStart && isEnd) return "event-single";
  if (isStart) return "event-start";
  if (isEnd) return "event-end";
  return "event-middle";
}

function getEventLabel(date, entry) {
  const start = normalizeDay(entry.start);
  const end = normalizeDay(entry.end);
  const current = normalizeDay(date);
  if (start.getTime() === end.getTime()) return entry.title;
  const middleTs = start.getTime() + Math.floor((end.getTime() - start.getTime()) / 2);
  const middle = new Date(middleTs);
  middle.setHours(0, 0, 0, 0);
  return current.getTime() === middle.getTime() ? entry.title : "";
}

function padDate(date) {
  return date.toISOString().split("T")[0];
}

function createMonthGrid(currentMonth) {
  const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
  const firstDay = (start.getDay() + 6) % 7;
  const cells = [];

  for (let i = 0; i < firstDay; i += 1) cells.push(null);
  for (let day = 1; day <= end.getDate(); day += 1) {
    cells.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const emptyForm = {
  id: "",
  title: "",
  description: "",
  start: "",
  end: "",
  location: "",
  color: "#1a73e8",
};

function Dittes({
  calendarSlug = "dittes",
  pageTitle = "Buchungskalender ESV-Bludenz",
  pageSubtitle = "Sektion Bergwandern",
  fallbackName = "Buchungskalender Dittes Hütte",
}) {
  const [events, setEvents] = useState([]);
  const [calendarInfo, setCalendarInfo] = useState({ name: fallbackName, description: "" });
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [adminMode, setAdminMode] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [authError, setAuthError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState("Lade Kalender...");
  const [showModal, setShowModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);

  const monthGrid = useMemo(() => createMonthGrid(currentMonth), [currentMonth]);
  const sortedEvents = useMemo(() => [...events].sort((a, b) => new Date(a.start) - new Date(b.start)), [events]);
  const visibleMonthLabel = new Intl.DateTimeFormat("de-AT", { month: "long", year: "numeric" }).format(currentMonth);

  async function loadEvents() {
    const response = await fetch(`${API_BASE}/events.php?calendar=${encodeURIComponent(calendarSlug)}`, {
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`API Fehler ${response.status}`);
    }

    const data = await response.json();
    const normalizedEvents = Array.isArray(data.events)
      ? data.events.map((entry) => ({
          ...entry,
          start: entry.start ? entry.start.replace(" ", "T") : entry.start,
          end: entry.end ? entry.end.replace(" ", "T") : entry.end,
        }))
      : [];

    setEvents(normalizedEvents);
    setCalendarInfo(data.calendar || { name: fallbackName, description: "" });
    setAdminMode(Boolean(data.isAdmin));
    setStatus("");
  }

  useEffect(() => {
    loadEvents().catch((error) => {
      console.error("Kalender laden fehlgeschlagen", error);
      setStatus(`Kalender konnte nicht geladen werden: ${error.message}`);
    });
  }, [calendarSlug, fallbackName]);

  function changeMonth(direction) {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1));
  }

  async function handleLogin(event) {
    event.preventDefault();
    const response = await fetch(`${API_BASE}/login.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ password: passwordInput, calendarSlug }),
    });

    if (!response.ok) {
      setAuthError("Passwort stimmt nicht.");
      return;
    }

    setPasswordInput("");
    setAuthError("");
    setShowLoginModal(false);
    await loadEvents();
  }

  async function handleLogout() {
    await fetch(`${API_BASE}/logout.php`, {
      method: "POST",
      credentials: "include",
    });
    setAdminMode(false);
    setForm(emptyForm);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!form.title || !form.start || !form.end) return;

    const payload = {
      title: form.title,
      description: form.description,
      location: form.location,
      start: form.start,
      end: form.end,
      color: form.color,
    };

    const url = form.id ? `${API_BASE}/update-event.php` : `${API_BASE}/save-event.php`;

    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ ...payload, id: form.id || undefined, calendarSlug }),
    });

    if (!response.ok) {
      setStatus("Speichern fehlgeschlagen.");
      return;
    }

    setForm(emptyForm);
    setShowModal(false);
    await loadEvents();
  }

  function handleEdit(eventItem) {
    setForm(eventItem);
    setShowModal(true);
  }

  function openCreateModal(date, endDate = null) {
    if (!adminMode || !date) return;
    const start = new Date(date);
    start.setHours(9, 0, 0, 0);
    const end = new Date(endDate || date);
    end.setHours(11, 0, 0, 0);
    if (end < start) {
      const tmp = new Date(start);
      start.setTime(end.getTime());
      end.setTime(tmp.getTime());
    }
    setForm({
      ...emptyForm,
      start: `${padDate(start)}T09:00`,
      end: `${padDate(end)}T11:00`,
    });
    setShowModal(true);
  }

  function isDateInDragRange(date) {
    if (!date || !dragStart || !dragEnd) return false;
    const current = new Date(date);
    current.setHours(0, 0, 0, 0);
    const start = new Date(dragStart);
    const end = new Date(dragEnd);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    const min = start < end ? start : end;
    const max = start > end ? start : end;
    return current >= min && current <= max;
  }

  function handleDayMouseDown(date) {
    if (!adminMode || !date) return;
    setDragStart(date);
    setDragEnd(date);
  }

  function handleDayMouseEnter(date) {
    if (!adminMode || !dragStart || !date) return;
    setDragEnd(date);
  }

  function handleDayMouseUp(date) {
    if (!adminMode || !dragStart || !date) return;
    const sameStartEnd = padDate(dragStart) === padDate(date);
    const startDate = dragStart;
    const endDate = date;
    setDragStart(null);
    setDragEnd(null);
    if (sameStartEnd) {
      openCreateModal(startDate);
      return;
    }
    openCreateModal(startDate, endDate);
  }

  async function handleDelete(id) {
    const response = await fetch(`${API_BASE}/delete-event.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ id }),
    });
    if (!response.ok) {
      setStatus("Löschen fehlgeschlagen.");
      return;
    }
    if (form.id === id) setForm(emptyForm);
    setShowModal(false);
    await loadEvents();
  }

  return (
    <div className="page-bg">
      <header className="dittes-header">
        <h2 className="mb-2">{pageTitle}</h2>
        <p className="mb-0">{pageSubtitle}</p>
      </header>

      <main className="container dittes-container pb-5">
        <section className="calendar-shell shadow-sm">
          <div className="calendar-topbar">
            <div>
              <h3 className="calendar-title">{calendarInfo.name}</h3>
              {calendarInfo.description ? <p className="calendar-subtitle mb-0">{calendarInfo.description}</p> : null}
            </div>
            <div className="calendar-controls">
              <button className="btn btn-light" onClick={() => changeMonth(-1)}>←</button>
              <strong className="month-label text-capitalize">{visibleMonthLabel}</strong>
              <button className="btn btn-light" onClick={() => changeMonth(1)}>→</button>
              {adminMode ? (
                <button className="btn btn-outline-secondary btn-sm" onClick={handleLogout}>Logout</button>
              ) : (
                <button className="btn btn-outline-danger btn-sm" onClick={() => setShowLoginModal(true)}>Admin</button>
              )}
            </div>
          </div>

          {status && <div className="px-3 py-2 text-muted">{status}</div>}

          <div className="calendar-grid-wrapper">
            <div className="weekday-row">
              {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((day) => (
                <div key={day} className="weekday-cell">{day}</div>
              ))}
            </div>

            <div className="month-grid">
              {monthGrid.map((date, index) => {
                const dayEvents = date ? sortedEvents.filter((entry) => isDateWithinEvent(date, entry.start, entry.end)) : [];
                return (
                  <div
                    key={`${date ? padDate(date) : `empty-${index}`}`}
                    className={`day-cell ${date ? "" : "day-cell-empty"} ${adminMode && date ? "day-cell-clickable" : ""} ${isDateInDragRange(date) ? "day-cell-selected" : ""}`}
                    onMouseDown={() => handleDayMouseDown(date)}
                    onMouseEnter={() => handleDayMouseEnter(date)}
                    onMouseUp={() => handleDayMouseUp(date)}
                  >
                    {date && (
                      <>
                        <div className="day-number">{date.getDate()}</div>
                        <div className="day-events">
                          {dayEvents.slice(0, 3).map((entry) => (
                            <div
                              key={entry.id}
                              className={`calendar-event-pill ${getEventSpanClass(date, entry.start, entry.end)} ${adminMode ? "calendar-event-pill-clickable" : ""}`}
                              style={{ backgroundColor: entry.color || "#1a73e8" }}
                              title={`${entry.title} | ${formatDateTime(entry.start)}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                if (adminMode) handleEdit(entry);
                              }}
                            >
                              {getEventLabel(date, entry)}
                            </div>
                          ))}
                          {dayEvents.length > 3 && <div className="calendar-more">+{dayEvents.length - 3} mehr</div>}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="calendar-shell shadow-sm h-100 mt-4">
          <div className="list-header">
            <h4 className="mb-1">Nächste Termine</h4>
          </div>
          <div className="event-list event-list-wide">
            {sortedEvents.slice(0, 10).map((entry) => (
              <article key={entry.id} className="event-card">
                <div className="event-color" style={{ backgroundColor: entry.color || "#1a73e8" }} />
                <div className="event-content">
                  <div className="event-header-row">
                    <div className="event-title-line">
                      <h5 className="mb-0">{entry.title}</h5>
                      <span className="event-separator">|</span>
                      <div className="event-meta-inline">{formatEventMeta(entry.start, entry.end)}</div>
                    </div>
                    {adminMode && (
                      <div className="event-actions">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => handleEdit(entry)}>Bearbeiten</button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(entry.id)}>Löschen</button>
                      </div>
                    )}
                  </div>
                  {entry.location && <div className="event-location">📍 {entry.location}</div>}
                  {entry.description && <p className="event-description mb-0">{entry.description}</p>}
                </div>
              </article>
            ))}
          </div>
        </section>

        <div className="d-flex justify-content-center mt-4">
          <Link to="/" className="btn btn-impressum px-4">
            Zurück zur Startseite
          </Link>
        </div>

        <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Admin Login</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form className="event-form modal-form" onSubmit={handleLogin}>
              <div>
                <label className="form-label">Passwort</label>
                <input
                  type="password"
                  className="form-control"
                  value={passwordInput}
                  onChange={(event) => setPasswordInput(event.target.value)}
                  placeholder="Passwort eingeben"
                />
              </div>
              {authError && <div className="auth-error">{authError}</div>}
              <div className="form-buttons">
                <button className="btn btn-danger" type="submit">Einloggen</button>
                <button className="btn btn-outline-secondary" type="button" onClick={() => setShowLoginModal(false)}>Abbrechen</button>
              </div>
            </form>
          </Modal.Body>
        </Modal>

        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{form.id ? "Termin bearbeiten" : "Neuen Termin anlegen"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form className="event-form modal-form" onSubmit={handleSubmit}>
              <div>
                <label className="form-label">Titel</label>
                <input className="form-control" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
              </div>
              <div>
                <label className="form-label">Ort</label>
                <input className="form-control" value={form.location} onChange={(event) => setForm({ ...form, location: event.target.value })} />
              </div>
              <div>
                <label className="form-label">Start</label>
                <input type="datetime-local" className="form-control" value={form.start} onChange={(event) => setForm({ ...form, start: event.target.value })} />
              </div>
              <div>
                <label className="form-label">Ende</label>
                <input type="datetime-local" className="form-control" value={form.end} onChange={(event) => setForm({ ...form, end: event.target.value })} />
              </div>
              <div>
                <label className="form-label">Farbe</label>
                <input type="color" className="form-control form-control-color" value={form.color} onChange={(event) => setForm({ ...form, color: event.target.value })} />
              </div>
              <div>
                <label className="form-label">Beschreibung</label>
                <textarea className="form-control" rows="4" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
              </div>
              <div className="form-buttons">
                <button className="btn btn-danger" type="submit">{form.id ? "Termin speichern" : "Termin anlegen"}</button>
                {form.id && <button className="btn btn-outline-danger" type="button" onClick={() => handleDelete(form.id)}>Löschen</button>}
                <button className="btn btn-outline-secondary" type="button" onClick={() => { setForm(emptyForm); setShowModal(false); }}>Abbrechen</button>
              </div>
            </form>
          </Modal.Body>
        </Modal>
      </main>
    </div>
  );
}

export default Dittes;
